import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import moment from "moment";
import { AppBar, APP } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { attendanceMeta, typeLabel } from "../../../utils/attendance";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { useSupportBusinessEndpoint } from "../../../utils/managerRole";
import {
  getBusinessAttendanceListApi,
  getSupportBusinessAttendanceListApi,
} from "../../../api/attendanceManager.api";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const BORDER = "#E1E1E1";

const buildMonthOptions = () => {
  const out = [];
  const now = moment();
  for (let i = 0; i < 12; i += 1) {
    const m = now.clone().subtract(i, "months");
    out.push({ value: m.format("MM-YYYY"), label: `Tháng ${m.format("M/YYYY")}` });
  }
  return out;
};

const AttendanceDetail = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const [search] = useSearchParams();
  const name = search.get("name") || "";
  const code = search.get("code") || "";
  const { user } = useEmployeeAuth();
  const fetchApi = useSupportBusinessEndpoint(user)
    ? getSupportBusinessAttendanceListApi
    : getBusinessAttendanceListApi;

  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [monthValue, setMonthValue] = useState(() => moment().format("MM-YYYY"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const sheet = useDisclosure();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const m = moment(monthValue, "MM-YYYY");
    try {
      const data = await fetchApi({
        employeeId: id,
        fromDate: m.clone().startOf("month").format("DD-MM-YYYY"),
        toDate: m.clone().endOf("month").format("DD-MM-YYYY"),
        page: 1,
        limit: 100,
      });
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [id, monthValue, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  const totalWork = useMemo(
    () => items.reduce((s, it) => s + (typeof it?.workingDayRate === "number" ? it.workingDayRate : 0), 0),
    [items]
  );

  const monthLabel = monthOptions.find((m) => m.value === monthValue)?.label || monthValue;

  const monthDrawer = (
    <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
      <DrawerOverlay bg="rgba(0,0,0,0.5)" />
      <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
        <DrawerBody py="16px" px="16px">
          <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">
            Chọn tháng
          </Text>
          <Box maxH="50vh" overflowY="auto">
            {monthOptions.map((opt, i) => {
              const active = opt.value === monthValue;
              const last = i === monthOptions.length - 1;
              return (
                <Flex
                  key={opt.value}
                  as="button"
                  w="100%"
                  align="center"
                  justify="space-between"
                  py="12px"
                  borderBottom={last ? "none" : "1px solid #EEF1F6"}
                  onClick={() => {
                    setMonthValue(opt.value);
                    sheet.onClose();
                  }}
                >
                  <Text
                    fontSize="15px"
                    color={active ? APP.primary200 : TEXT}
                    fontWeight={active ? 600 : 400}
                  >
                    {opt.label}
                  </Text>
                </Flex>
              );
            })}
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it, i) => {
      const meta = attendanceMeta(it?.attendanceType);
      return {
        id: it?._id || i,
        meta,
        date: it?.date ? moment(it.date).format("DD/MM/YYYY") : "-",
        time: it?.checkinTime ? moment(it.checkinTime).format("HH:mm") : "-",
        label:
          it?.attendanceType === "HOLIDAY"
            ? it?.holiday?.name || "Cuối tuần / Lễ"
            : typeLabel(it?.attendanceType),
        rate: typeof it?.workingDayRate === "number" ? String(it.workingDayRate) : "0",
      };
    });

    return (
      <DesktopPage
        title={`Chấm công nhân viên${name ? ` - ${name}` : ""}${code ? ` (${code})` : ""}`}
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        <Flex align="flex-end" gap="16px" flexWrap="wrap">
          <DesktopStatRow
            items={[
              { key: "work", title: "Tổng công", total: Math.round(totalWork * 100) / 100 },
            ]}
          />
          <Box
            as="button"
            w="220px"
            h="44px"
            px="12px"
            textAlign="left"
            bg="white"
            border="1px solid"
            borderColor={BORDER}
            borderRadius="8px"
            onClick={sheet.onOpen}
          >
            <Flex align="center" justify="space-between">
              <Text fontSize="14px" color={TEXT}>
                {monthLabel}
              </Text>
              <FiChevronDown size={18} color={MUTED} />
            </Flex>
          </Box>
        </Flex>

        <DesktopPanel p="0">
          <DesktopTable
            loading={loading}
            emptyText="Không có dữ liệu chấm công"
            rows={rows}
            headers={[
              { key: "date", label: "Ngày", width: "25%" },
              { key: "time", label: "Giờ vào", width: "20%" },
              { key: "rate", label: "Số công", width: "15%" },
              {
                key: "label",
                label: "Phân loại",
                width: "40%",
                render: (row) => (
                  <Box
                    display="inline-block"
                    bg={(row.meta?.color || "#94A3B8") + "22"}
                    borderRadius="6px"
                    px="10px"
                    py="4px"
                  >
                    <Text fontSize="13px" fontWeight={600} color={row.meta?.color || MUTED}>
                      {row.label}
                    </Text>
                  </Box>
                ),
              },
            ]}
          />
        </DesktopPanel>

        {monthDrawer}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Chấm công nhân viên" />
      <Box px="16px" pt="12px">
        {(name || code) && (
          <Box bg="#F1F5FB" borderRadius="12px" p="12px" mb="12px">
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{name}{code ? ` - ${code}` : ""}</Text>
          </Box>
        )}

        <Flex gap="10px" align="center">
          <Flex as="button" flex="1" h="40px" px="10px" align="center" justify="space-between" bg="white" border="1px solid" borderColor={BORDER} borderRadius="8px" onClick={sheet.onOpen}>
            <Text fontSize="14px" color={TEXT}>{monthLabel}</Text>
            <FiChevronDown size={18} color={MUTED} />
          </Flex>
          <Box flex="1" bg="white" border="1px solid" borderColor={BORDER} borderRadius="8px" px="10px" py="6px">
            <Text fontSize="11px" color={MUTED}>Tổng công</Text>
            <Text fontSize="15px" fontWeight={700} color={APP.primary200}>{Math.round(totalWork * 100) / 100}</Text>
          </Box>
        </Flex>

        <Box mt="16px">
          {loading ? (
            <Flex justify="center" py="32px"><Spinner color={APP.primary200} /></Flex>
          ) : items.length === 0 ? (
            <Text fontSize="14px" color={MUTED} textAlign="center" pt="20px">Không có dữ liệu chấm công</Text>
          ) : (
            <Flex direction="column" gap="8px">
              {items.map((it, i) => {
                const meta = attendanceMeta(it?.attendanceType);
                const label = it?.attendanceType === "HOLIDAY"
                  ? it?.holiday?.name || "Cuối tuần / Lễ"
                  : typeLabel(it?.attendanceType);
                return (
                  <Flex key={it?._id || i} align="center" justify="space-between" bg="white" border="1px solid" borderColor={BORDER} borderRadius="10px" px="12px" py="10px">
                    <Box>
                      <Text fontSize="14px" fontWeight={600} color={TEXT}>{it?.date ? moment(it.date).format("DD/MM/YYYY") : "-"}</Text>
                      {it?.checkinTime ? (
                        <Text fontSize="12px" color={MUTED} mt="2px">Giờ vào: {moment(it.checkinTime).format("HH:mm")}</Text>
                      ) : null}
                    </Box>
                    <Box bg={(meta?.color || "#94A3B8") + "22"} borderRadius="6px" px="10px" py="4px">
                      <Text fontSize="12px" fontWeight={600} color={meta?.color || MUTED}>{label}</Text>
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Box>
      </Box>

      {monthDrawer}
    </Box>
  );
};

export default AttendanceDetail;
